// src/pages/Admin/Cities/AdminStreetCreate.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/utils/axios";

// Reuse same SCSS as AdminCreateCity
import cls from "./AdminStreetCreate.module.scss";

const STATUSES = ["open", "closed"];

const AdminStreetCreate = () => {
  const navigate = useNavigate();
  const { cityId } = useParams();

  const [cityMeta, setCityMeta] = useState(null);

  const [streetName, setStreetName] = useState("");
  const [streetStatus, setStreetStatus] = useState("open");
  const [streetVibe, setStreetVibe] = useState("");
  const [streetIntro, setStreetIntro] = useState("");
  const [streetDescription, setStreetDescription] = useState("");
  const [sortOrder, setSortOrder] = useState(0);
  const [isFeatured, setIsFeatured] = useState(false);
  const [tags, setTags] = useState("");

  const [err, setErr] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const handleBack = () => navigate(-1);

  // Fetch city to show metadata and determine streetLanguage
  useEffect(() => {
    const loadCity = async () => {
      try {
        const { data } = await api.get(`/v1/admin/cities/${cityId}`);
        setCityMeta(data);
      } catch (e) {
        console.error("Failed to load city", e);
      }
    };
    loadCity();
  }, [cityId]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setSuccess("");

    if (!streetName.trim()) {
      setErr("Street name is required.");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        streetName: streetName.trim(),
        streetStatus,
        streetVibe,
        streetIntro,
        streetDescription,
        sortOrder: Number(sortOrder) || 0,
        isFeatured,
        tags: tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
      };

      const { data } = await api.post(
        `/v1/admin/cities/${cityId}/streets`,
        payload
      );

      const id = data?._id;
      setSuccess("Street created. Redirecting to details…");
      navigate(
        id
          ? `/admin/cities/single/${cityId}/streets/${id}`
          : `/admin/cities/single/${cityId}/streets`
      );
    } catch (e) {
      console.error("Create street failed", e);
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "Failed to create street.";
      setErr(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={cls.page}>
      {/* HEADER */}
      <div className={cls.header}>
        <button type="button" className={cls.backBtn} onClick={handleBack}>
          ← Back
        </button>

        <h1 className={cls.title}>Create Street</h1>
        <p className={cls.subtitle}>
          You are creating a street inside:
          <br />
          <strong>{cityMeta?.cityName || "Loading city…"}</strong>
        </p>
      </div>

      {/* META BAR */}
      <div className={cls.metaBar}>
        <div className={cls.metaItem}>
          <span className={cls.metaLabel}>Mode</span>
          <span className={cls.metaValue}>Create new street</span>
        </div>

        <div className={cls.metaItem}>
          <span className={cls.metaLabel}>Language</span>
          <span className={cls.metaValue}>
            {cityMeta?.cityLanguage?.toUpperCase?.() || "…"}
          </span>
        </div>

        <div className={cls.metaItem}>
          <span className={cls.metaLabel}>Status</span>
          <span className={cls.metaValue}>{streetStatus}</span>
        </div>

        <div className={cls.metaItem}>
          <span className={cls.metaLabel}>Featured</span>
          <span className={cls.metaValue}>{isFeatured ? "Yes" : "No"}</span>
        </div>
      </div>

      {err && <div className={cls.error}>⚠ {err}</div>}
      {success && <div className={cls.success}>{success}</div>}

      <div className={cls.layout}>
        {/* LEFT CARD – IMAGES PREVIEW */}
        <section className={cls.avatarCard}>
          <div className={cls.avatarHeader}>
            <h2 className={cls.cardTitle}>Street Images</h2>
            <p className={cls.cardSubtitle}>
              After creating the street, you can upload avatar, thumbnail,
              cover, and Noty images on its detail page.
            </p>
          </div>

          <div className={cls.avatarPreviewWrap}>
            <div className={cls.avatarGlow}>
              <div className={cls.avatarFallback}>No avatar yet</div>
            </div>
            <p className={cls.avatarMeta}>
              Uploads become available after saving this street.
            </p>
          </div>

          <div className={cls.imagesGrid}>
            <div className={cls.imageSlot}>
              <p className={cls.imageLabel}>Avatar Thumb (480.webp)</p>
              <div className={cls.imagePreviewMini}>
                <div className={cls.miniFallback}>Pending</div>
              </div>
              <p className={cls.imageHint}>Used on small preview cards.</p>
            </div>

            <div className={cls.imageSlot}>
              <p className={cls.imageLabel}>Cover Image</p>
              <div className={cls.imagePreviewMini}>
                <div className={cls.miniFallback}>Pending</div>
              </div>
              <p className={cls.imageHint}>
                Large banner when entering this street.
              </p>
            </div>

            <div className={cls.imageSlot}>
              <p className={cls.imageLabel}>Mr Noty Image</p>
              <div className={cls.imagePreviewMini}>
                <div className={cls.miniFallback}>Pending</div>
              </div>
              <p className={cls.imageHint}>Male persona for this street.</p>
            </div>

            <div className={cls.imageSlot}>
              <p className={cls.imageLabel}>Mrs Noty Image</p>
              <div className={cls.imagePreviewMini}>
                <div className={cls.miniFallback}>Pending</div>
              </div>
              <p className={cls.imageHint}>Female persona for this street.</p>
            </div>
          </div>
        </section>

        {/* RIGHT CARD – FORM */}
        <section className={cls.formCard}>
          <div className={cls.formHeader}>
            <h2 className={cls.cardTitle}>Street Details</h2>
            <p className={cls.cardSubtitle}>
              Configure the info for this street. You can fine-tune later.
            </p>
          </div>

          <form className={cls.form} onSubmit={onSubmit}>
            {/* Name + Status */}
            <div className={cls.formRow}>
              <div className={cls.field}>
                <label className={cls.label}>Street name</label>
                <input
                  type="text"
                  className={cls.input}
                  value={streetName}
                  onChange={(e) => setStreetName(e.target.value)}
                  placeholder="Neon Alley"
                  required
                />
              </div>

              <div className={cls.field}>
                <label className={cls.label}>Status</label>
                <select
                  className={cls.select}
                  value={streetStatus}
                  onChange={(e) => setStreetStatus(e.target.value)}
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Sort + Featured */}
            <div className={cls.formRow}>
              <div className={cls.field}>
                <label className={cls.label}>Sort order</label>
                <input
                  type="number"
                  className={cls.input}
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                />
                <p className={cls.help}>Lower appears earlier</p>
              </div>

              <div className={cls.field}>
                <label className={cls.label}>Featured street</label>
                <div className={cls.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={isFeatured}
                    onChange={(e) => setIsFeatured(e.target.checked)}
                  />
                  <span className={cls.help}>Highlight across city views.</span>
                </div>
              </div>
            </div>

            {/* Vibe */}
            <div className={cls.field}>
              <label className={cls.label}>Vibe / tagline</label>
              <input
                type="text"
                className={cls.input}
                value={streetVibe}
                onChange={(e) => setStreetVibe(e.target.value)}
                placeholder="Where neon energy never sleeps…"
              />
            </div>

            {/* Intro */}
            <div className={cls.field}>
              <label className={cls.label}>Intro</label>
              <textarea
                rows={3}
                className={cls.input}
                value={streetIntro}
                onChange={(e) => setStreetIntro(e.target.value)}
                placeholder="Short intro shown first."
              />
            </div>

            {/* Description */}
            <div className={cls.field}>
              <label className={cls.label}>Description</label>
              <textarea
                rows={4}
                className={cls.input}
                value={streetDescription}
                onChange={(e) => setStreetDescription(e.target.value)}
                placeholder="Longer explanation of the street vibe."
              />
            </div>

            {/* Tags */}
            <div className={cls.field}>
              <label className={cls.label}>Tags (comma separated)</label>
              <input
                type="text"
                className={cls.input}
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="neon, nightlife, music"
              />
            </div>

            {/* ACTIONS */}
            <div className={cls.actions}>
              <button
                type="button"
                className={cls.secondaryBtn}
                onClick={handleBack}
                disabled={loading}
              >
                Cancel
              </button>
              <button className={cls.primaryBtn} type="submit" disabled={loading}>
                {loading ? "Creating…" : "Create Street"}
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
};

export default AdminStreetCreate;
